import {
  CONTENT_MEDIA_FIELD_NAMES,
  contentMediaAttachmentIds,
  isUserMediaContent,
} from '@mondaygpt/types'
import { apiCall } from 'modules/core/services/api/api'
import { ProjectService } from 'openapi/services/ProjectService'
import type { Creative } from '../types'
import { flushPersistsAndWait, useCreativeStore } from './creative-store'
import { useIdeaStore } from './idea-store'

class UserMediaRequiredError extends Error {}

/**
 * The swap was refused for a reason the user can act on (already published,
 * about to publish, media a platform can't take, no media left), not a fault.
 */
export function isUserMediaRefusal(err: unknown): boolean {
  return err instanceof UserMediaRequiredError || (err as { status?: number })?.status === 400
}

/** True when `after` changes the media of an upload post's creative. */
export function isUserMediaChange(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): boolean {
  if (!isUserMediaContent(before)) return false
  return contentMediaAttachmentIds(after).join(',') !== contentMediaAttachmentIds(before).join(',')
}

/**
 * An upload post's media is shared by every platform, so a media change goes
 * through the post (the same call as ByoMediaTray) and only the other edits
 * save on this creative. Rejects when the swap is refused.
 */
export async function saveUserMediaEdit(args: {
  creativeId: string
  projectId: string
  before: Record<string, unknown>
  after: Record<string, unknown>
}): Promise<void> {
  const nextMedia = contentMediaAttachmentIds(args.after)
  if (nextMedia.length === 0) {
    throw new UserMediaRequiredError(
      'This post publishes your own upload, so it needs a photo or video.'
    )
  }
  const words: Record<string, unknown> = { ...args.after }
  for (const key of CONTENT_MEDIA_FIELD_NAMES) {
    if (key in args.before) words[key] = args.before[key]
    else delete words[key]
  }
  useCreativeStore
    .getState()
    .updateCreative(args.creativeId, { content: words as unknown as Creative['content'] })
  useIdeaStore.getState().patchCreativeContent(args.creativeId, words)

  const projectCreativeIds = useCreativeStore
    .getState()
    .creatives.filter(c => c.projectId === args.projectId)
    .map(c => c.id)
  await flushPersistsAndWait(projectCreativeIds)
  await apiCall(ProjectService.projectControllerUpdateUserMediaV1, null, args.projectId, {
    attachmentIds: nextMedia,
  })
  await useIdeaStore.getState().loadIdea(args.projectId)
}
